import moment from 'moment';

import { BadgeProps } from 'lib/components/Badge';
import { PublisherInvoiceView } from 'lib/types/publisherInvoices';
import {
  ALL_INVOICE_CREATION_DATES,
  ALL_INVOICES_STATUS,
  LAST_MONTH_INVOICE_CREATION_DATES,
  PAID_INVOICES_STATUS,
  PAST_DUE_INVOICES_STATUS,
  THIS_MONTH_INVOICE_CREATION_DATES,
  UNPAID_INVOICES_STATUS
} from './invoiceTableSettingsUtils';

export const VOID_PUBLISHER_INVOICES_STATUS = 'void';

export type PublisherInvoiceTableStatus =
  | typeof ALL_INVOICES_STATUS
  | typeof UNPAID_INVOICES_STATUS
  | typeof PAST_DUE_INVOICES_STATUS
  | typeof PAID_INVOICES_STATUS
  | typeof VOID_PUBLISHER_INVOICES_STATUS;

export const PUBLISHER_INVOICE_STATUS_OPTIONS: {
  label: string;
  value: PublisherInvoiceTableStatus;
}[] = [
  { label: 'All', value: ALL_INVOICES_STATUS },
  { label: 'Past Due', value: PAST_DUE_INVOICES_STATUS },
  { label: 'Unpaid', value: UNPAID_INVOICES_STATUS },
  { label: 'Paid', value: PAID_INVOICES_STATUS },
  { label: 'Void', value: VOID_PUBLISHER_INVOICES_STATUS }
];

export type PublisherInvoiceTableFilter = {
  // filter to only include invoices with any of the selected statuses
  statuses: PublisherInvoiceTableStatus[];

  // filter based on invoice creation dates within a particular range
  dateRange:
    | {
        start: Date;
        end: Date;
      }
    | typeof ALL_INVOICE_CREATION_DATES
    | typeof THIS_MONTH_INVOICE_CREATION_DATES
    | typeof LAST_MONTH_INVOICE_CREATION_DATES;
};

export const DEFAULT_PUBLISHER_INVOICE_FILTER: PublisherInvoiceTableFilter = {
  statuses: [ALL_INVOICES_STATUS],
  dateRange: ALL_INVOICE_CREATION_DATES
};

/**
 * The display status of a publisher invoice; open invoices past their due
 * date display as past due, mirroring the customer table's semantics.
 */
export const getPublisherInvoiceDisplayStatus = (
  invoice: PublisherInvoiceView
): Exclude<PublisherInvoiceTableStatus, typeof ALL_INVOICES_STATUS> => {
  if (invoice.status === 'open') {
    const isPastDue =
      invoice.dueDate !== null &&
      moment.utc(invoice.dueDate * 1000).isBefore(moment.utc());
    return isPastDue ? PAST_DUE_INVOICES_STATUS : UNPAID_INVOICES_STATUS;
  }
  return invoice.status;
};

export const getPublisherInvoiceStatusLabel = (
  invoice: PublisherInvoiceView
): string => {
  const displayStatus = getPublisherInvoiceDisplayStatus(invoice);
  const option = PUBLISHER_INVOICE_STATUS_OPTIONS.find(
    ({ value }) => value === displayStatus
  );
  return option?.label ?? displayStatus;
};

export const getPublisherInvoiceBadgeStatus = (
  invoice: PublisherInvoiceView
): BadgeProps['status'] => {
  switch (getPublisherInvoiceDisplayStatus(invoice)) {
    case PAID_INVOICES_STATUS:
      return 'success';
    case UNPAID_INVOICES_STATUS:
      return 'warning';
    case PAST_DUE_INVOICES_STATUS:
      return 'critical';
    default:
      // void renders as neutral gray
      return undefined;
  }
};

const invoiceMatchesDateRange = (
  invoice: PublisherInvoiceView,
  dateRange: PublisherInvoiceTableFilter['dateRange']
): boolean => {
  const createdAt = moment.utc(invoice.createdAt * 1000);
  if (dateRange === ALL_INVOICE_CREATION_DATES) {
    return true;
  }
  if (dateRange === THIS_MONTH_INVOICE_CREATION_DATES) {
    return createdAt.isSameOrAfter(moment().utc().startOf('month'));
  }
  if (dateRange === LAST_MONTH_INVOICE_CREATION_DATES) {
    return (
      createdAt.isSameOrAfter(
        moment().utc().subtract(1, 'month').startOf('month')
      ) && createdAt.isBefore(moment().utc().startOf('month'))
    );
  }
  return (
    createdAt.isSameOrAfter(moment.utc(dateRange.start).startOf('day')) &&
    createdAt.isSameOrBefore(moment.utc(dateRange.end).endOf('day'))
  );
};

export const publisherInvoiceMatchesFilter = (
  invoice: PublisherInvoiceView,
  filter: PublisherInvoiceTableFilter
): boolean => {
  const effectiveStatuses = filter.statuses.filter(
    s => s !== ALL_INVOICES_STATUS
  );
  if (
    effectiveStatuses.length > 0 &&
    !effectiveStatuses.includes(getPublisherInvoiceDisplayStatus(invoice))
  ) {
    return false;
  }

  return invoiceMatchesDateRange(invoice, filter.dateRange);
};

export const getNumActivePublisherInvoiceFilters = (
  filter: PublisherInvoiceTableFilter
): number => {
  let numActive = 0;

  if (filter.dateRange !== ALL_INVOICE_CREATION_DATES) {
    numActive++;
  }

  if (filter.statuses.some(s => s !== ALL_INVOICES_STATUS)) {
    numActive++;
  }

  return numActive;
};
